import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { observer } from "mobx-react-lite";
import {
  Box,
  Chip,
  Typography,
  Select,
  MenuItem,
  Button,
  Skeleton,
  Tooltip,
  IconButton,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { DEGREE_STYLES, degreeNumberMap } from "./PriorityDegreeBar";
import PriorityDegreeBar from "./PriorityDegreeBar";
import CustomDataGridPro from "../CustomDataGridPro";
import alertNotificationStore from "../../stores/AlertNotificationStore";
import authStore from "../../stores/AuthStore";

const renderBoldHeader = (params) => (
  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
    {params?.colDef?.headerName || ""}
  </Typography>
);

const AlertNotificationTab = observer(() => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const alerts = alertNotificationStore.alerts;
  const reachedStart = alertNotificationStore.reachedStart;
  const earliestId = alertNotificationStore.earliestAlertId;

  // useEffect(() => {
  //   deviceStore.deleteDeviceNameFromCache("PR-005");
  // }, []);

  const handleLoadMore = async () => {
    await alertNotificationStore.fetchAllAlerts(
      [],
      null,
      null,
      100,
      earliestId
    );
  };

  const columns = useMemo(
    () => [
      {
        field: "priority",
        headerName: "Priority",
        width: 130,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const numericPriority = Number(params.value);
          const key = degreeNumberMap[numericPriority]; // 1 -> "low"
          const config = DEGREE_STYLES[key];

          if (!config) {
            return (
              <Chip
                label="Unknown"
                sx={{
                  height: 28,
                  borderRadius: "16px",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  px: 1.5,
                  backgroundColor: "#f0f0f0",
                  color: "#666",
                  border: "1px solid #ccc",
                }}
              />
            );
          }

          const cleanLabel = key.charAt(0).toUpperCase() + key.slice(1);
          const isResolved = params.row?.isResolved;

          return (
            <Chip
              label={
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  >
                    {cleanLabel}
                  </Typography>
                  {isResolved && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.7rem",
                        color: "#388e3c",
                        lineHeight: 1,
                      }}
                    >
                      Resolved
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                height: "auto",
                py: 0.5,
                borderRadius: "16px",
                px: 1.5,
                backgroundColor: config.bg,
                color: config.text,
                border: `1px solid ${config.border}`,
                textTransform: "capitalize",
                alignItems: "center",
              }}
            />
          );
        },
      },
      {
        field: "device",
        headerName: "Device",
        width: 100,
        renderHeader: renderBoldHeader,
      },
      {
        field: "lastNotified",
        headerName: "Time",
        width: 170,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const dateStr = new Date(params.value).toLocaleString();
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography sx={{ fontSize: "0.875rem", fontWeight: "inherit" }}>
                {dateStr}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "field",
        headerName: "Field",
        width: 160,
        renderHeader: renderBoldHeader,
      },
      {
        field: "type",
        headerName: "Type",
        width: 160,
        renderHeader: renderBoldHeader,
      },
      {
        field: "value",
        headerName: "Error Value",
        width: 160,
        renderHeader: renderBoldHeader,
      },
      {
        field: "expected",
        headerName: "Expected",
        width: 160,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const expected = params.value;
          if (!expected) return "—";

          // Fallback for plain strings
          if (typeof expected === "string") return expected;

          // For MobX proxies or plain objects
          const hasMin = expected.min != null;
          const hasMax = expected.max != null;
          const faultCode = expected.faultCode;

          if (faultCode) return `Fault ${faultCode}`;
          if (hasMin && hasMax)
            return `min ${expected.min}, max ${expected.max}`;
          if (hasMin) return `min ${expected.min}`;
          if (hasMax) return `max ${expected.max}`;

          return JSON.stringify(expected); // Fallback
        },
      },
      {
        field: "message",
        headerName: "Message",
        flex: 1,
        renderHeader: renderBoldHeader,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: renderBoldHeader,
        renderCell: (params) => (
          <Box display="flex" gap={1}>
            {/* View Config Button */}
            <Tooltip title="View Config">
              <IconButton
                onClick={async () => {
                  const uid = authStore.user?.id || authStore.user?.userID;
                  try {
                    await alertNotificationStore.markAlertAsRead(
                      params.row.id,
                      uid
                    );
                  } finally {
                    navigate(`/alerts/${params.row.id}`, {
                      state: {
                        deviceId: params.row.device_id,
                        deviceName: params.row.device,
                      },
                    });
                  }
                }}
                size="small"
                sx={{ color: theme.palette.primary.main }}
              >
                <VisibilityOutlinedIcon />
              </IconButton>
            </Tooltip>
      
            {params.row.isRead && (
              <Button
                onClick={() =>
                  alertNotificationStore.markAlertAsUnread(
                    params.row.id,
                    authStore.user?.id || authStore.user?.userID
                  )
                }
                size="small"
                sx={{ textTransform: "none" }}
              >
                Mark Unread
              </Button>
            )}
          </Box>
        ),
      },
    ],
    []
  );

  // Convert alerts to DataGrid rows
  const rows = useMemo(() => {
    return alerts.map((alert) => {
      const rawId = typeof alert._id === "object" ? alert._id?.$oid : alert._id;
      const isRead = alertNotificationStore.isAlertReadByUser(rawId);
      const isResolved = alert?.active === false;

      return {
        id: rawId,
        device_id: alert.device_id || "undefined",
        priority: alert.priority || 1,
        field: alert.field || "",
        type: alert.alert_type || "",
        value: alert.value || "",
        expected: alert.expected || alert.get?.["expected"],
        message: alert.message || alert.triggerMessage || "",
        device:
          alert.device_name ||
          (alert.device_id
            ? `Pending Device ${alert.device_id.slice(-4)}`
            : "Unknown Device"),
        lastNotified: alert.last_notified_at?.$date || "",
        rawAlert: alert,
        isRead,
        isResolved,
      };
    });
  }, [alerts, alertNotificationStore.readAlertIds.size]);

  const totalPages = Math.max(Math.ceil(rows.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <PriorityDegreeBar degree={null} />
      <Box sx={{ height: 540, width: "100%" }}>
        {alertNotificationStore.isLoading ? (
          <Box
            sx={{
              height: 540,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#fafafa",
              boxShadow: 2,
            }}
          >
            {/* Fake Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#f4f6f8",
                borderBottom: "1px solid #ddd",
                px: 2,
                py: 1,
              }}
            >
              {columns.map((col, i) => (
                <Skeleton
                  key={i}
                  variant="text"
                  width={col.width || 100}
                  height={28}
                  sx={{ flexShrink: 0, mr: 2 }}
                />
              ))}
            </Box>

            {/* Fake Rows */}
            {Array.from({ length: pageSize }).map((_, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f7fbff",
                }}
              >
                {columns.map((col, colIndex) => (
                  <Skeleton
                    key={colIndex}
                    variant="rectangular"
                    width={col.width || 100}
                    height={24}
                    sx={{ flexShrink: 0, mr: 2, borderRadius: 1 }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <CustomDataGridPro
            rows={rows}
            columns={columns}
            isLoading={alertNotificationStore.isLoading}
            isSkeletonOnly={!alertNotificationStore.isReadStatusLoaded}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            getRowClassName={(params) => {
              const row = params.row;
              const classes = [];
              if (row.isResolved) classes.push("resolved-alert-row");
              if (!row.isRead) classes.push("unread-alert-row");
              return classes.join(" ");
            }}
            totalCount={rows.length}
          />
        )}
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
        }}
      >
        <Button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Prev
        </Button>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <Button
              key={p}
              onClick={() => setPage(p)}
              variant={p === currentPage ? "contained" : "text"}
            >
              {p}
            </Button>
          ))}
        </Box>
        <Button
          onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography>Rows per page:</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      {!reachedStart && (
        <Box mt={2} display="flex" justifyContent="center">
          <Button
            onClick={handleLoadMore}
            variant="outlined"
            disabled={alertNotificationStore.isLoading}
          >
            {alertNotificationStore.isLoading ? "Loading..." : "Load More"}
          </Button>
        </Box>
      )}
    </Box>
  );
});

export default AlertNotificationTab;
